const TARGET_WIDTH = 1600
const QUALITY = 0.88
const GAP = 12

export async function joinSides(sides: File[]): Promise<File> {
  const present = sides.filter(Boolean)

  if (present.length < 2) {
    return present[0] as File
  }

  const images = await Promise.all(present.map(decode))
  const width = Math.min(TARGET_WIDTH, Math.max(...images.map((image) => image.width)))

  const scaled = images.map((image) => ({
    image,
    width,
    height: Math.round((image.height * width) / image.width),
  }))

  const height = scaled.reduce((total, one) => total + one.height, 0) + GAP * (scaled.length - 1)

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  const context = canvas.getContext('2d')

  if (!context) {
    return present[0] as File
  }

  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, width, height)

  let top = 0

  for (const one of scaled) {
    context.drawImage(one.image, 0, top, one.width, one.height)
    top += one.height + GAP
  }

  images.forEach((image) => image.close())

  return (await encode(canvas, 'document.jpg')) ?? (present[0] as File)
}

export async function normalizePhoto(file: File, name: string): Promise<File> {
  let image: ImageBitmap

  try {
    image = await decode(file)
  } catch {
    return file
  }

  const width = Math.min(TARGET_WIDTH, image.width)
  const height = Math.round((image.height * width) / image.width)

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  const context = canvas.getContext('2d')

  if (!context) {
    image.close()

    return file
  }

  context.drawImage(image, 0, 0, width, height)
  image.close()

  return (await encode(canvas, name)) ?? file
}

async function encode(canvas: HTMLCanvasElement, name: string): Promise<File | undefined> {
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', QUALITY),
  )

  return blob ? new File([blob], name, { type: 'image/jpeg' }) : undefined
}

function decode(file: File): Promise<ImageBitmap> {
  return createImageBitmap(file, { imageOrientation: 'from-image' })
}
